/* Earnings: what the platform owes your agency and what it has paid.
 *
 * A job your staff complete earns your agency the family's price for it (less
 * the platform's commission, if any). The platform collects every payment from
 * the family and pays the agency; you pay your own staff. When a family paid
 * one of your people in CASH, your agency already holds that money, so the
 * platform takes it off your next payment (000077): each payment shows what
 * was earned, the cash netted, and the transfer. Every figure is the server's
 * (GET /agency/earnings). */

import { useState } from 'react';
import { getEarnings } from '../data/agency';
import { dateOnly, dateTime, money } from '../data/format';
import { moneyStatus, serviceName } from '../data/words';
import { Card, CardHead, CellStack, Empty, Kpi, KpiGrid, Note, Stack, Table, Tabs, Tag, TRow, uiCss } from '../ui';
import { ServiceDot, useAsync } from './common';

const LINE_COLS = '1.1fr 1.2fr 1.2fr 1.3fr 0.9fr 0.9fr';
const PAY_COLS = '1fr 1fr 0.9fr 0.8fr 1fr 1.4fr 0.5fr';
const CASH_COLS = '1.1fr 1.2fr 1.3fr 0.9fr 1.4fr';

function statusTag(s: string) {
  const t =
    s === 'PAID'
      ? { fg: 'var(--ok)', bg: 'var(--ok-bg)' }
      : s === 'VOID'
        ? { fg: 'var(--ink-faint)', bg: 'var(--surface-field)' }
        : { fg: 'var(--amber)', bg: 'var(--amber-bg)' };
  return (
    <Tag fg={t.fg} bg={t.bg}>
      {moneyStatus(s)}
    </Tag>
  );
}

/** Where one cash collection stands, in words. */
function cashTag(c: { status: string; settlement: string | null }) {
  const t =
    c.settlement === 'AGENCY_NETTED'
      ? { fg: 'var(--ok)', bg: 'var(--ok-bg)', w: 'Taken off a payment' }
      : c.settlement === 'DEPOSITED'
        ? { fg: 'var(--ok)', bg: 'var(--ok-bg)', w: 'Handed in' }
        : // ck_cc_settlement's third value: settled off the collector's own pay,
          // not yours — it was never the agency's to hand in.
          c.settlement === 'PAYOUT_NETTED'
          ? { fg: 'var(--ok)', bg: 'var(--ok-bg)', w: "Taken off the worker's pay" }
        : c.status === 'DISPUTED'
          ? { fg: 'var(--danger)', bg: 'var(--danger-bg)', w: 'Count disputed' }
          : { fg: 'var(--amber)', bg: 'var(--amber-bg)', w: 'Held by your agency' };
  return (
    <Tag fg={t.fg} bg={t.bg}>
      {t.w}
    </Tag>
  );
}

const isZero = (s: string | null | undefined) => !s || Number(s) === 0;

export function EarningsScreen() {
  const e = useAsync(getEarnings, []);
  const [tab, setTab] = useState<'work' | 'payments' | 'cash'>('work');
  const d = e.data;

  return (
    <Stack>
      <KpiGrid>
        <Kpi
          label="Next payment"
          value={d ? money(d.summary.owed) : '—'}
          note={d && !isZero(d.summary.cash_held) ? `${money(d.summary.accrued)} earned, less cash your people collected` : 'completed work not yet paid'}
          color={d && Number(d.summary.owed) > 0 ? 'var(--amber)' : undefined}
        />
        <Kpi label="Paid to you" value={d ? money(d.summary.paid) : '—'} note="work settled by the platform" color={d ? 'var(--ok)' : undefined} />
        <Kpi label="Cash your people hold" value={d ? money(d.summary.cash_held) : '—'} note="collected from families, not yet settled" />
        <Kpi label="Earned this month" value={d ? money(d.summary.this_month) : '—'} note="since the 1st, India time" />
      </KpiGrid>

      <Note>
        The platform collects every payment from families and pays your agency for the jobs your staff complete; you pay your own staff.
        When a family pays one of your people in cash, that cash is the platform's and your agency holds it, so it is taken off your next
        payment. Payments are made by bank transfer and recorded here with their reference.
      </Note>
      {d && !isZero(d.summary.cash_beyond_earnings) ? (
        <Note tone="amber">
          Your people hold {money(d.summary.cash_beyond_earnings)} more in cash than your agency is currently owed. It will be taken off later
          payments, or the platform will ask for it to be handed in.
        </Note>
      ) : null}
      {e.error ? <Note tone="danger">Your earnings could not be read: {e.error}</Note> : null}

      <Card>
        <CardHead
          title={tab === 'work' ? 'Completed work' : tab === 'payments' ? 'Payments to your agency' : 'Cash your people collected'}
          sub={
            tab === 'work'
              ? 'Each completed shift, visit or trip, with what it earned you.'
              : tab === 'payments'
                ? 'Every payment the platform made to you: what was earned, the cash taken off, and the transfer with its bank reference.'
                : 'Cash families paid your people on your jobs, and where each collection stands.'
          }
          right={
            <Tabs
              items={[
                { key: 'work', label: `Work done${d ? ` · ${d.lines.length}` : ''}` },
                { key: 'payments', label: `Payments${d ? ` · ${d.payouts.length}` : ''}` },
                { key: 'cash', label: `Cash${d ? ` · ${d.cash.length}` : ''}` },
              ]}
              value={tab}
              onChange={setTab}
            />
          }
        />
        {!d ? (
          <Empty>{e.loading ? 'Reading your earnings…' : '—'}</Empty>
        ) : tab === 'work' ? (
          d.lines.length === 0 ? (
            <Empty>Nothing yet. Each completed shift, visit or trip appears here.</Empty>
          ) : (
            <Table cols={LINE_COLS} head={['When', 'Service', 'Done by', 'Booking', 'Amount', 'Status']} minWidth={820}>
              {d.lines.map((l) => (
                <TRow key={l.public_id} cols={LINE_COLS}>
                  <span className={uiCss.sub}>{dateTime(l.earned_at)}</span>
                  <span style={{ font: '700 13px var(--font-ui)' }}>
                    <ServiceDot code={l.service_code} />
                    {serviceName(l.service_code)}
                  </span>
                  <span>{l.staff_name || '—'}</span>
                  <span className={uiCss.mono}>{l.booking_public_id}</span>
                  <span className={uiCss.num} style={{ textAlign: 'left' }}>
                    {money(l.amount)}
                  </span>
                  <span>{statusTag(l.status)}</span>
                </TRow>
              ))}
            </Table>
          )
        ) : tab === 'payments' ? (
          d.payouts.length === 0 ? (
            <Empty>No payments yet.</Empty>
          ) : (
            <Table cols={PAY_COLS} head={['Transferred', 'Earned', 'Cash taken off', 'Status', 'Paid on', 'Bank reference', 'Jobs']} minWidth={820}>
              {d.payouts.map((p) => (
                <TRow key={p.public_id} cols={PAY_COLS}>
                  <CellStack top={<strong>{money(p.amount)}</strong>} />
                  <span className={uiCss.num} style={{ textAlign: 'left' }}>
                    {money(p.earnings_amount)}
                  </span>
                  <span className={uiCss.num} style={{ textAlign: 'left' }}>
                    {isZero(p.cash_netted_amount) ? '—' : `${money(p.cash_netted_amount)} (${p.cash_collections})`}
                  </span>
                  <span>{statusTag(p.status)}</span>
                  <span className={uiCss.sub}>{dateOnly(p.paid_at)}</span>
                  <span className={uiCss.mono}>{p.reference || (isZero(p.amount) ? 'nothing transferred' : '—')}</span>
                  <span className={uiCss.num} style={{ textAlign: 'left' }}>
                    {p.lines}
                  </span>
                </TRow>
              ))}
            </Table>
          )
        ) : d.cash.length === 0 ? (
          <Empty>No cash collected on your jobs.</Empty>
        ) : (
          <Table cols={CASH_COLS} head={['Collected', 'By', 'Booking', 'Amount', 'Where it stands']} minWidth={720}>
            {d.cash.map((c) => (
              <TRow key={c.public_id} cols={CASH_COLS}>
                <span className={uiCss.sub}>{dateTime(c.collected_at)}</span>
                <span>{c.staff_name || '—'}</span>
                <span className={uiCss.mono}>{c.booking_public_id}</span>
                <span className={uiCss.num} style={{ textAlign: 'left' }}>
                  {money(c.amount)}
                </span>
                <span>{cashTag(c)}</span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>
    </Stack>
  );
}
